import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  LocalFireDepartment as FireIcon,
  CheckCircleOutline as OpenIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonButton,
  CommonChip,
} from "../../../../Components/ui";

const MEGA_SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "highestPrize", label: "Highest Prize Pool" },
  { value: "lowestEntry", label: "Lowest Entry Fee" },
  { value: "capacityHigh", label: "Largest Matrix (Capacity)" },
  { value: "capacityLow", label: "Smallest Matrix (Higher Odds)" },
  { value: "pickHigh", label: "Most Picks Required" },
];

const MegaLotteryFilters = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortChange,
  onRefresh,
  loading = false,
  totalCount = 0,
  rolloverCount = 0,
  openCount = 0,
}) => {
  return (
    <Box
      sx={{
        bgcolor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        p: { xs: 2, md: 2.5 },
        mb: 4,
        boxShadow: customShadows.cardFlat,
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        {/* Search Field */}
        <Grid item xs={12} md={4}>
          <CommonInput
            placeholder="Search by Lottery #ID, Partner name, or Address..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
              </InputAdornment>
            }
            endAdornment={
              searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange("")}
                    edge="end"
                    sx={{ color: colors.slate[400] }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            fullWidth
            size="small"
          />
        </Grid>

        {/* Status Filter Tabs / CommonChips matching platform standard */}
        <Grid item xs={12} sm={7} md={4}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            sx={{ flexWrap: "wrap", py: 0.5 }}
          >
            <CommonChip
              variant="filter"
              label={`All (${totalCount})`}
              selected={statusFilter === "all"}
              onClick={() => onStatusFilterChange("all")}
              sx={
                statusFilter === "all"
                  ? {
                      bgcolor: `${colors.emerald[600]} !important`,
                      "&:hover": { bgcolor: `${colors.emerald[700]} !important` },
                    }
                  : undefined
              }
            />

            <CommonChip
              variant="filter"
              label={`Rollover (${rolloverCount})`}
              selected={statusFilter === "rollover"}
              icon={<FireIcon sx={{ fontSize: 14 }} />}
              onClick={() => onStatusFilterChange("rollover")}
              sx={
                statusFilter === "rollover"
                  ? {
                      bgcolor: `${colors.emerald[600]} !important`,
                      "&:hover": { bgcolor: `${colors.emerald[700]} !important` },
                    }
                  : undefined
              }
            />

            <CommonChip
              variant="filter"
              label={`Standard Open (${openCount})`}
              selected={statusFilter === "open"}
              icon={<OpenIcon sx={{ fontSize: 14 }} />}
              onClick={() => onStatusFilterChange("open")}
              sx={
                statusFilter === "open"
                  ? {
                      bgcolor: `${colors.emerald[600]} !important`,
                      "&:hover": { bgcolor: `${colors.emerald[700]} !important` },
                    }
                  : undefined
              }
            />
          </Stack>
        </Grid>

        {/* Sorting & Refresh */}
        <Grid item xs={12} sm={5} md={4}>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            alignItems="center"
          >
            <Box sx={{ minWidth: 190, flexGrow: { xs: 1, sm: 0 } }}>
              <CommonSelect
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                options={MEGA_SORT_OPTIONS}
                size="small"
              />
            </Box>
            <CommonButton
              variant="outline"
              size="small"
              onClick={onRefresh}
              disabled={loading}
              sx={{ p: 1, minWidth: 40 }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: loading ? "spin 1s linear infinite" : undefined,
                }}
              />
            </CommonButton>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

MegaLotteryFilters.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  totalCount: PropTypes.number,
  rolloverCount: PropTypes.number,
  openCount: PropTypes.number,
};

export default MegaLotteryFilters;
